import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { Link, useLocation } from "react-router";

import { docNeighbours, type DocPageId, docsPath } from "@/lib/docs-pages";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { useDocsContent } from "./content";
import { FOCUS_DOC_TITLE, focusesDocTitle } from "./focus";
import { DOC_PAGE_PARTS } from "./pages";

interface DocPageProps {
  page: DocPageId;
}

/**
 * One page of the docs: its title, its content, and the links to the
 * previous and the next page at the bottom. The app shell scrolls a new page
 * to its top.
 */
export function DocPage({ page }: DocPageProps) {
  const { pages } = useDocsContent();
  const { state } = useLocation();
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { Body } = DOC_PAGE_PARTS[page];

  useEffect(() => {
    logger.debug("[docs]", "page", page);
    if (focusesDocTitle(state)) {
      titleRef.current?.focus({ preventScroll: true });
    }
  }, [page, state]);

  return (
    <article aria-labelledby={titleId} className="max-w-3xl">
      <h2
        id={titleId}
        ref={titleRef}
        tabIndex={-1}
        className="font-heading text-primary text-xl font-bold focus:outline-none"
      >
        {pages[page]}
      </h2>
      <div className="text-foreground mt-3 space-y-4 text-sm leading-6">
        <Body />
      </div>
      <DocPager page={page} />
    </article>
  );
}

/**
 * The links to the previous and the next page, side by side when there is
 * room; the first page has only a next link (on the right), the last only a
 * previous one.
 */
function DocPager({ page }: DocPageProps) {
  const { page: text, pages } = useDocsContent();
  const { previous, next } = docNeighbours(page);
  return (
    <nav
      aria-label={text.pagerLabel}
      className="border-border mt-12 grid gap-3 border-t pt-6 @min-[560px]:grid-cols-2"
    >
      {previous && (
        <PagerLink page={previous} label={text.previous} title={pages[previous]} side="previous" />
      )}
      {next && <PagerLink page={next} label={text.next} title={pages[next]} side="next" />}
    </nav>
  );
}

interface PagerLinkProps {
  page: DocPageId;
  /** "Previous" or "Next", small above the title. */
  label: string;
  title: string;
  side: "previous" | "next";
}

/** A link to the previous or the next page, with its title and an arrow its way. */
function PagerLink({ page, label, title, side }: PagerLinkProps) {
  const Arrow = side === "previous" ? ArrowLeft : ArrowRight;
  return (
    <Link
      to={docsPath(page)}
      state={FOCUS_DOC_TITLE}
      className={cn(
        "border-border hover:bg-accent-light focus-visible:ring-ring flex flex-col gap-1 rounded-lg border px-4 py-3 transition-colors focus-visible:ring-2 focus-visible:outline-none",
        side === "next" && "items-end text-right @min-[560px]:col-start-2",
      )}
    >
      <span
        className={cn(
          "text-muted flex items-center gap-1 text-xs font-semibold",
          side === "next" && "flex-row-reverse",
        )}
      >
        <Arrow className="size-3.5 shrink-0" aria-hidden="true" />
        {label}
      </span>
      <span className="text-primary text-sm font-semibold">{title}</span>
    </Link>
  );
}
